import React, { FC, useEffect, useState } from 'react';
import { useWatch } from 'rc-field-form';
import { RouteComponentProps } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { disableInputInvalidValueForWeight } from 'src/app/common/utils';
import DateTimeRangePicker from 'src/app/common/components/DateTimeRangePicker';
import Form from 'src/app/common/components/Form';
import { KEY_TASK_SUMMARIZER_BASE_PATH, OPERATION_BASE_PATH } from '../../../constants';
import {
  CategoryEnum,
  DisplayFormatEnum,
  KeyTaskConfiguration,
  FormModeEnum,
  PublishedStatusEnum,
} from '../../../types/key-task-summarizer-type';
import {
  createKeyTaskSummarizer,
  fetchKeyTaskSummarizer,
  updateKeyTaskSummarizer,
} from '../../../network/keyTaskSummarizerCrud';
import {
  categories,
  configurationForSave,
  createConfiguration,
  getCategoryTranslationKey,
} from '../../../utils/key-task-summarizer-utils';
import CustomerOfferFields from './Fields/CustomerOfferFields';
import ManagementMessageFields from './Fields/ManagementMessageFields';
import TimetableFields from './Fields/TimetableFields';
import {
  createConfigurationRules,
  createDefaultRequiredRules,
  createEffectiveRangeRules,
} from './keyTaskSummarizerDetailForm.rules';
import { useStyles } from './keyTaskSummarizerDetailForm.style';

const formModeTranslationKeys: Record<FormModeEnum, string> = {
  [FormModeEnum.CREATE]: 'global.add.text',
  [FormModeEnum.EDIT]: 'global.edit.text',
  [FormModeEnum.VIEW]: 'global.view.text',
};

const isKeyTaskNotFoundError = (error: unknown): boolean => {
  return (
    !!error &&
    typeof error === 'object' &&
    'statusCode' in error &&
    error.statusCode === 400 &&
    'message' in error &&
    error.message === 'key task summary content not found'
  );
};

type KeyTaskSummarizerDetailFormProps = RouteComponentProps<{ id?: string; from?: string }> &
  ModulePermissionProps & {
    formMode: FormModeEnum;
  };

type KeyTaskFormValues = KeyTaskConfiguration & {
  effectiveRange: [Date | null, Date | null];
  linkParamsField?: string;
};

const KeyTaskSummarizerDetailForm: FC<KeyTaskSummarizerDetailFormProps> = ({
  history,
  match,
  enableRead,
  enableCreate,
  enableUpdate,
  formMode,
}) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const category = useWatch('category', form);
  const countdownDate = useWatch('countdownDate', form);
  const displayFormat = useWatch('displayFormat', form);
  const publishedStatus = useWatch('publishedStatus', form);
  const [loadedId, setLoadedId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(formMode !== FormModeEnum.CREATE);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const id = match.params.id;
  const KEY_TASK_PATH = OPERATION_BASE_PATH + KEY_TASK_SUMMARIZER_BASE_PATH;
  const supportsMessageVariables = category === CategoryEnum.TIMETABLE;

  const canAccess =
    formMode === FormModeEnum.CREATE
      ? enableCreate
      : formMode === FormModeEnum.EDIT
        ? enableUpdate
        : enableRead || enableCreate || enableUpdate;
  const isDisabled = formMode === FormModeEnum.VIEW || isSubmitting;

  const configurationRules = createConfigurationRules(() => {
    const values = form.getFieldsValue(true) as KeyTaskFormValues;
    const [startDate, endDate] = values.effectiveRange || [null, null];
    return {
      ...values,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
    };
  }, Translation);

  const [initialFormValues] = useState<KeyTaskFormValues>(() => ({
    ...createConfiguration(),
    effectiveRange: [null, null],
  }));

  useEffect(() => {
    let active = true;
    const defaultValues = createConfiguration();
    form.setFieldsValue({
      ...defaultValues,
      effectiveRange: [null, null],
    });
    setLoadedId(undefined);
    setSubmitError(false);
    setLoadError(false);
    setNotFound(false);
    if (formMode === FormModeEnum.CREATE || !canAccess) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const fetchKeyTask = async () => {
      try {
        if (!id) {
          setNotFound(true);
          return;
        }
        const record = await fetchKeyTaskSummarizer(id, dispatch);
        if (!active) return;
        if (record) {
          form.setFieldsValue({
            ...record,
            selectedCampaign: record.selectedCampaign
              ? { _id: record.selectedCampaign }
              : undefined,
            effectiveRange: [record.effectiveStartDate, record.effectiveEndDate],
          });
          setLoadedId(record.id);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        if (active) {
          if (isKeyTaskNotFoundError(error)) {
            setNotFound(true);
          } else {
            console.log(error)
            setLoadError(true);
          }
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    void fetchKeyTask();
    return () => {
      active = false;
    };
  }, [dispatch, form, id, formMode, canAccess]);

  const onChangeFields = (fields: Partial<KeyTaskConfiguration>) => {
    form.setFieldsValue(fields);
  };

  const onCancel = () => {
    if (!isSubmitting) history.push(`${KEY_TASK_PATH}/list`);
  };

  const onSubmitForm = async () => {
    const values = form.getFieldsValue(true) as KeyTaskFormValues;
    if (
      !canAccess ||
      isDisabled ||
      isLoading ||
      loadError ||
      notFound ||
      (formMode === FormModeEnum.EDIT && loadedId !== id)
    )
      return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const { effectiveRange, ...configuration } = values;
      const recordConfiguration = { ...configuration };
      delete recordConfiguration.linkParamsField;
      const [startDate, endDate] = effectiveRange || [null, null];
      const payload = configurationForSave({
        ...recordConfiguration,
        effectiveStartDate: startDate,
        effectiveEndDate: endDate,
      });
      if (formMode === FormModeEnum.EDIT) {
        if (!id) throw new Error('Missing Key Task ID for update');
        await updateKeyTaskSummarizer(id, payload);
      } else {
        await createKeyTaskSummarizer(payload);
      }
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('global.submit.success'),
            content: Translation(
              formMode === FormModeEnum.CREATE
                ? 'global.submit.saved'
                : 'global.submit.updated',
            ),
          },
        ]),
      );
      history.push(`${KEY_TASK_PATH}/list`);
    } catch {
      setSubmitError(true);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('global.submit.fail'),
            content: Translation('global.submit.fail'),
          },
        ]),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canAccess) return <Alert severity="error">{Translation('KeyTaskSummerizer.no_access')}</Alert>;

  const unavailable = loadError || notFound;

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={commonClasses.header}>
          {Translation(formModeTranslationKeys[formMode])} {Translation('KeyTaskSummerizer.title')}
        </div>
        <Button variant="contained" color="inherit" disabled={isSubmitting} onClick={onCancel}>
          {Translation('global.back.btnText')}
        </Button>
      </div>
      {notFound && (
        <Alert severity="error" className={classes.alert}>
          {Translation('KeyTaskSummerizer.record_not_found')}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" className={classes.alert}>
          {Translation('common.loading.refreshAgain')}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" className={classes.alert} onClose={() => setSubmitError(false)}>
          {Translation('global.submit.fail')}
        </Alert>
      )}
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress aria-label={Translation('common.loading')} />
        </div>
      ) : (
        !unavailable && (
          <Form
            form={form}
            initialValues={initialFormValues}
            onFinish={onSubmitForm}
          >
            <div className={classes.section}>
              <Typography component="h6" className={classes.sectionTitle}>
                {Translation('KeyTaskSummerizer.type')}
              </Typography>
              {/* Category selection */}
              <Form.Item
                name="category"
                label={Translation('KeyTaskSummerizer.category')}
                rules={configurationRules('category')}
              >
                <RadioGroup
                  row
                  aria-label={Translation('KeyTaskSummerizer.category')}
                  onChange={() => {
                    setSubmitError(false);
                    onChangeFields({
                      selectedCampaign: null,
                      countdownDate: null,
                      redirectTo: '',
                      link: '',
                      params: null,
                      openBy: '',
                      canShare: '',
                    });
                  }}
                >
                  {categories.map((category) => (
                    <FormControlLabel
                      key={category}
                      value={category}
                      control={<Radio disabled={isDisabled} />}
                      label={Translation(getCategoryTranslationKey(category))}
                    />
                  ))}
                </RadioGroup>
              </Form.Item>

              {/* English message field */}
              <Form.Item
                name="contentEn"
                label={Translation('KeyTaskSummerizer.contentEn')}
                style={{ flex: 1 }}
                labelTop
                rules={configurationRules('contentEn')}
              >
                {(control) => (
                  <div className={classes.inputContainer}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      disabled={isDisabled}
                      margin="dense"
                      variant="outlined"
                      value={control.value}
                      onChange={(event) => {
                        control.onChange(event.target.value);
                      }}
                      inputProps={{ 'aria-label': Translation('KeyTaskSummerizer.contentEn') }}
                    />
                  </div>
                )}
              </Form.Item>

              {/* Chinese message field */}
              <Form.Item
                name="contentZhHk"
                label={Translation('KeyTaskSummerizer.contentZhHk')}
                labelTop
                rules={configurationRules('contentZhHk')}
              >
                {(control) => (
                  <div className={classes.inputContainer}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      disabled={isDisabled}
                      margin="dense"
                      variant="outlined"
                      value={control.value}
                      onChange={(event) => {
                        control.onChange(event.target.value);
                      }}
                      inputProps={{ 'aria-label': Translation('KeyTaskSummerizer.contentZhHk') }}
                    />
                    {supportsMessageVariables && (
                      <Typography variant="body2" color="text.secondary">
                        {Translation('KeyTaskSummerizer.variable_hint')}
                      </Typography>
                    )}
                  </div>
                )}
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(previous, current) =>
                  previous.category !== current.category ||
                  previous.selectedCampaign !== current.selectedCampaign ||
                  previous.link !== current.link ||
                  previous.params !== current.params
                }
              >
                {(_, __, { getFieldsValue }) => {
                  const values = getFieldsValue(true) as KeyTaskFormValues;
                  return (
                    <>
                      {values.category === CategoryEnum.CUSTOMER_OFFER && (
                        <CustomerOfferFields
                          isDisabled={isDisabled}
                          campaign={values.selectedCampaign}
                          configurationRules={configurationRules}
                          onChangeFields={onChangeFields}
                        />
                      )}

                      {values.category === CategoryEnum.TIMETABLE && (
                        <TimetableFields
                          isDisabled={isDisabled}
                          configurationRules={configurationRules}
                        />
                      )}

                      {values.category === CategoryEnum.MANAGEMENT_MESSAGE && (
                        <ManagementMessageFields
                          form={form}
                          isDisabled={isDisabled}
                          initialLink={values.link || ''}
                          initialLinkParams={values.params}
                          configurationRules={configurationRules}
                          onChangeFields={onChangeFields}
                        />
                      )}
                    </>
                  );
                }}
              </Form.Item>

              <Typography component="h6" className={classes.sectionTitle}>
                {Translation('KeyTaskSummerizer.publish_setting')}
              </Typography>

              {/* Display order field */}
              <Form.Item
                name="order"
                label={Translation('KeyTaskSummerizer.display_order')}
                rules={configurationRules('order')}
              >
                <TextField
                  type="number"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  disabled={isDisabled}
                  inputProps={{
                    ...disableInputInvalidValueForWeight(),
                    'aria-label': Translation('KeyTaskSummerizer.display_order'),
                  }}
                />
              </Form.Item>

              {/* Effective date range field */}
              <Form.Item
                name="effectiveRange"
                labelTop
                label={Translation('component.formLabel.effective-date')}
                rules={createEffectiveRangeRules(Translation)}
              >
                {(control) => (
                  <div className={classes.inputContainer}>
                    <DateTimeRangePicker
                      value={control.value}
                      onChange={control.onChange}
                      disabled={isDisabled}
                    />
                    {supportsMessageVariables && countdownDate && displayFormat === DisplayFormatEnum.DISPLAY_DATE && (
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage(
                          { id: 'KeyTaskSummerizer.countdown_date_hint' },
                          { sup: (chunks: React.ReactNode[]) => <sup>{chunks}</sup> },
                        )}
                      </Typography>
                    )}
                  </div>
                )}
              </Form.Item>

              {/* Publish now field */}
              {formMode !== FormModeEnum.VIEW && (
                <Form.Item
                  name="publishedStatus"
                  label={Translation('component.formLabel.publish-now')}
                  rules={createDefaultRequiredRules(Translation)}
                >
                  <RadioGroup
                    row
                    aria-label={Translation('component.formLabel.publish-now')}
                    name="publishedStatus"
                  >
                    <FormControlLabel
                      value={PublishedStatusEnum.PUBLISHED}
                      control={<Radio disabled={isDisabled} />}
                      label={Translation('component.formSelectItem.save-publish')}
                    />
                    <FormControlLabel
                      value={PublishedStatusEnum.UNPUBLISHED}
                      control={<Radio disabled={isDisabled} />}
                      label={Translation('component.formSelectItem.save-as-draft')}
                    />
                  </RadioGroup>
                </Form.Item>
              )}
            </div>

            {/* Footer section */}
            {formMode !== FormModeEnum.VIEW && (
              <div className={classes.footerContainer}>
                <Button variant="contained" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {Translation('app.button.cancel')}
                </Button>
                <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
                  {Translation(publishedStatus === PublishedStatusEnum.PUBLISHED ? 'app.button.submit' : 'component.formSelectItem.save-as-draft')}
                  {isSubmitting && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                </Button>
              </div>
            )}
          </Form>
        )
      )}
    </div>
  );
};

export default KeyTaskSummarizerDetailForm;
